import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchEmergencyAlertsAPI, fetchEmergencyMessagesAPI, getEmergencyMessageCountsAPI } from '../services/alertService';

export default function EmergencyAlertsPage() {
    const { serverId } = useParams();
    const navigate = useNavigate();
    
    // Strict Pagination States
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [msgCounts, setMsgCounts] = useState({});
    
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    // Exact Match Search States
    const [searchField, setSearchField] = useState('username');
    const [searchValue, setSearchValue] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Modal States
    const [selectedAlert, setSelectedAlert] = useState(null); 
    const [messages, setMessages] = useState([]); 
    const [msgsLoading, setMsgsLoading] = useState(false);

    // Master Fetch Function
    const loadAlerts = async (isLoadMore = false) => {
        if (isLoadMore) setLoadingMore(true);
        else setLoading(true);

        const currentCursor = isLoadMore ? lastDoc : null;
        
        const searchConfig = isSearching && searchValue.trim() !== '' 
            ? { field: searchField, value: searchValue } 
            : null;

        const response = await fetchEmergencyAlertsAPI(serverId, currentCursor, searchConfig);
        
        // Fetch message counts for the new batch
        if (response.data.length > 0) {
            const ids = response.data.map(a => a.id);
            const counts = await getEmergencyMessageCountsAPI(serverId, ids);
            setMsgCounts(prev => ({ ...prev, ...counts }));
        }

        if (isLoadMore) {
            setAlerts(prev => [...prev, ...response.data]);
        } else {
            setAlerts(response.data);
        }

        setLastDoc(response.lastVisible);
        setHasMore(response.hasMore);
        
        setLoading(false);
        setLoadingMore(false);
    };

    useEffect(() => {
        loadAlerts(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId, isSearching]);

    // Search Handlers
    const handleExecuteSearch = (e) => {
        e.preventDefault();
        if (searchValue.trim() === '') return;
        setLastDoc(null);
        setIsSearching(true);
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setLastDoc(null);
        setIsSearching(false);
    };

    // 3. Handle "View Messages" Click
    const handleViewMessages = async (alert) => {
        setSelectedAlert(alert);
        setMsgsLoading(true);
        const msgs = await fetchEmergencyMessagesAPI(serverId, alert.id);
        setMessages(msgs);
        setMsgsLoading(false);
    };

    const closeModal = () => {
        setSelectedAlert(null);
        setMessages([]);
    };

    const parseDate = (val) => {
        if (!val) return new Date();
        if (val.toDate) return val.toDate();
        return new Date(val);
    };

    const getMapLink = (lat, long) => `https://www.google.com/maps?q=${lat},${long}`;

    return (
        <div style={{ padding: '30px', backgroundColor: '#fff5f5', minHeight: '100vh', position: 'relative' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '25px' }}>
                <button 
                    onClick={() => navigate(-1)} 
                    style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #fc8181', borderRadius: '4px', background: 'white', color: '#c53030', cursor: 'pointer' }}
                >
                    ← Back
                </button>
                <h1 style={{ margin: 0, color: '#c53030', fontSize: '24px' }}>🚨 Emergency SOS Alerts</h1>
            </div>

            {/* EXACT MATCH SEARCH BLOCK */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fed7d7', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'bold', color: '#c53030', fontSize: '14px' }}>Exact Search:</span>
                <select 
                    value={searchField} 
                    onChange={(e) => setSearchField(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #feb2b2', backgroundColor: '#fff5f5', minWidth: '150px' }}
                    disabled={isSearching}
                >
                    <option value="username">Username</option>
                    <option value="phoneNumber">Phone Number (+44...)</option>
                    <option value="staffId">Staff ID</option>
                </select>
                <form onSubmit={handleExecuteSearch} style={{ display: 'flex', gap: '10px', flex: 1, minWidth: '200px' }}>
                    <input 
                        type="text" 
                        placeholder={`Exact match for ${searchField}...`}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        disabled={isSearching}
                        style={{ padding: '8px 12px', flex: 1, borderRadius: '4px', border: '1px solid #feb2b2', backgroundColor: isSearching ? '#fff5f5' : 'white' }}
                    />
                    {!isSearching ? (
                        <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#c53030', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Search</button>
                    ) : (
                        <button type="button" onClick={handleClearSearch} style={{ padding: '8px 16px', backgroundColor: '#fed7d7', color: '#9b2c2c', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Clear Search</button>
                    )}
                </form>
            </div>

            {/* List */}
            {loading ? <p style={{ color: '#c53030' }}>Loading emergencies...</p> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {alerts.length > 0 ? alerts.map(item => {
                        const count = msgCounts[item.id] || 0;
                        const hasMessages = count > 0;

                        return (
                            <div key={item.id} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', borderLeft: '6px solid #e53e3e' }}>
                                {/* Top Row: Status & Time */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '12px', backgroundColor: '#fed7d7', color: '#9b2c2c', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                                        {item.callStatus?.toUpperCase() || 'EMERGENCY'}
                                    </span>
                                    <span style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>
                                        {parseDate(item.time).toLocaleString()}
                                    </span>
                                </div>
                                
                                {/* Title */}
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#2d3748' }}>
                                    SOS: {item.fullName} <span style={{ fontSize: '14px', color: '#718096', fontWeight: 'normal' }}>({item.username})</span>
                                </h3>

                                {/* Action Buttons Row */}
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px', alignItems: 'center' }}>
                                    <a href={`tel:${item.phoneNumber}`} style={{ textDecoration: 'none', color: 'white', backgroundColor: '#48bb78', padding: '8px 12px', borderRadius: '4px', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        📞 Call {item.phoneNumber}
                                    </a>
                                    
                                    {/* CLICKABLE LOCATION */}
                                    {item.location ? (
                                        <a 
                                            href={getMapLink(item.location.latitude, item.location.longitude)} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ backgroundColor: '#edf2f7', padding: '8px 12px', borderRadius: '4px', fontSize: '13px', color: '#2b6cb0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '600' }}
                                        >
                                            📍 Open in Maps ({item.location.latitude.toFixed(5)}, {item.location.longitude.toFixed(5)})
                                        </a>
                                    ) : (
                                        <div style={{ backgroundColor: '#edf2f7', padding: '8px 12px', borderRadius: '4px', fontSize: '13px', color: '#a0aec0' }}>
                                            📍 No GPS Data
                                        </div>
                                    )}
                                </div>

                                {/* Bottom Row: Status Banner & Messages Button */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #edf2f7' }}>
                                    {!item.resolved ? (
                                        <span style={{ color: '#c53030', fontWeight: 'bold', fontSize: '14px' }}>⚠️ Active - Unresolved</span>
                                    ) : (
                                        <span style={{ color: '#48bb78', fontWeight: 'bold', fontSize: '14px' }}>✅ Resolved</span>
                                    )}

                                    {/* VIEW MESSAGES BUTTON */}
                                    <button 
                                        onClick={() => hasMessages && handleViewMessages(item)}
                                        disabled={!hasMessages}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '20px',
                                            border: 'none',
                                            backgroundColor: hasMessages ? '#3182ce' : '#e2e8f0',
                                            color: hasMessages ? 'white' : '#a0aec0',
                                            cursor: hasMessages ? 'pointer' : 'default',
                                            fontWeight: '600',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {hasMessages ? `View Messages (${count})` : 'No Messages (0)'}
                                    </button>
                                </div>
                            </div>
                        );
                    }) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0', backgroundColor: 'white', borderRadius: '8px' }}>
                            ✅ No active emergency alerts found matching your criteria.
                        </div>
                    )}
                    {hasMore && alerts.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
                            <button 
                                onClick={() => loadAlerts(true)}
                                disabled={loadingMore}
                                style={{ padding: '10px 24px', borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: 'white', color: '#4a5568', fontWeight: 'bold', cursor: loadingMore ? 'not-allowed' : 'pointer' }}
                            >
                                {loadingMore ? 'Loading...' : 'Load Next 50'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* --- MESSAGE HISTORY MODAL --- */}
            {selectedAlert && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ backgroundColor: 'white', width: '500px', maxWidth: '90%', maxHeight: '80vh', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {/* Modal Header */}
                        <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f7fafc' }}>
                            <h3 style={{ margin: 0, color: '#2d3748' }}>Conversation Log</h3>
                            <button onClick={closeModal} style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', color: '#a0aec0' }}>×</button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {msgsLoading ? (
                                <p style={{ textAlign: 'center', color: '#718096' }}>Loading messages...</p>
                            ) : messages.length > 0 ? (
                                messages.map((msg, idx) => {
                                    const isAdmin = msg.senderRole === 'admin' || msg.isAdminOnly;
                                    const isLoc = msg.type === 'location';

                                    return (
                                        <div key={idx} style={{ 
                                            alignSelf: isAdmin ? 'flex-end' : 'flex-start',
                                            maxWidth: '80%',
                                            backgroundColor: isAdmin ? '#ebf8ff' : '#f0fff4', 
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: `1px solid ${isAdmin ? '#bee3f8' : '#9ae6b4'}`
                                        }}>
                                            <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px', fontWeight: 'bold' }}>
                                                {msg.senderId} ({msg.senderRole}) • {parseDate(msg.createdAt).toLocaleTimeString()}
                                            </div>
                                            
                                            {/* Message Content */}
                                            {isLoc && msg.location ? (
                                                <a 
                                                    href={getMapLink(msg.location.latitude, msg.location.longitude)}
                                                    target="_blank" rel="noopener noreferrer"
                                                    style={{ color: '#2b6cb0', textDecoration: 'underline', fontWeight: '600' }}
                                                >
                                                    📍 GPS Beacon (Click to Map)
                                                </a>
                                            ) : (
                                                <div style={{ color: '#2d3748', whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <p style={{ textAlign: 'center', color: '#a0aec0' }}>No messages found.</p>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div style={{ padding: '15px', borderTop: '1px solid #eee', textAlign: 'center', backgroundColor: '#f7fafc' }}>
                            <button onClick={closeModal} style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #cbd5e0', background: 'white', cursor: 'pointer' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}